// src/components/RequireAuth.jsx
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    isAuthenticated,
    getActiveRole,
    getRoles,
    shouldPromptRole,
    targetPathForRole,
} from '../utils/auth';
import { getSetupStatus } from '../api/setup';

/**
 * Module-level cache — survives re-renders, resets on full page reload (logout).
 * null  = not yet checked
 * true  = setup is done
 * false = setup is needed
 *
 * Also mirrored in sessionStorage so it survives React remounts within the same
 * browser session (e.g. when navigating away from /setup to /admin).
 */
let _setupComplete = null;
/** Timestamp of last markSetupComplete() — avoids bouncing back to /setup right after finishing the wizard. */
let _setupMarkedAt = 0;

/** Clear setup cache — call on login so a DB reset is picked up on next session. */
export function resetSetupCache() {
    _setupComplete = null;
    _setupMarkedAt = 0;
    sessionStorage.removeItem('_setupComplete');
    localStorage.removeItem('_setupComplete');
}

function readCache() {
    // Only trust 'true' — a stale 'false' must always be re-verified with the API
    // so a single DB reset or test doesn't permanently lock users in the wizard.
    if (_setupComplete === true) return true;
    const stored = sessionStorage.getItem('_setupComplete') ?? localStorage.getItem('_setupComplete');
    if (stored === 'true') {
        _setupComplete = true;
        return true;
    }
    return null; // null = must call API
}

function writeCache(value) {
    _setupComplete = value;
    sessionStorage.setItem('_setupComplete', String(value));
    localStorage.setItem('_setupComplete', String(value));
}

/** Called by SetupWizard after finishing so the cache is marked done. */
export function markSetupComplete() {
    writeCache(true);
    _setupMarkedAt = Date.now();
}

function recentlyFinishedWizard() {
    return _setupMarkedAt > 0 && Date.now() - _setupMarkedAt < 30000;
}

const RequireAuth = ({ allowedRoles, children }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const pathname = (location?.pathname || '').replace(/\/+$/, '') || '/';

    const activeRole = getActiveRole();
    const isAdmin = activeRole === 'ADMIN';
    const onSetupPage = pathname === '/setup';

    // ── Hooks must ALWAYS be called unconditionally (Rules of Hooks) ─────────
    const cached = readCache(); // true | null
    const [setupChecked, setSetupChecked] = useState(cached === true);
    const [setupComplete, setSetupComplete] = useState(cached); // true | null

    useEffect(() => {
        if (!isAdmin || onSetupPage) {
            setSetupChecked(true);
            if (!onSetupPage) {
                setSetupComplete(readCache());
            }
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const data = await getSetupStatus();
                if (cancelled) return;
                const done = !!data.setupComplete;
                if (done) {
                    writeCache(true);
                    setSetupComplete(true);
                } else if (recentlyFinishedWizard() || readCache() === true) {
                    // DB may lag briefly after wizard save — don't bounce the admin out.
                    setSetupComplete(true);
                } else {
                    setSetupComplete(false);
                }
            } catch {
                if (cancelled) return;
                // Trust cached "complete" on transient errors; otherwise assume incomplete
                // so the wizard is shown — but never leave the user on a spinner forever.
                setSetupComplete(readCache() === true ? true : false);
            } finally {
                if (!cancelled) setSetupChecked(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAdmin, onSetupPage]); // eslint-disable-line
    // ─────────────────────────────────────────────────────────────────────────

    // 1) Must be logged in
    if (!isAuthenticated()) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    // 2) Let the role picker render freely
    if (pathname === '/select-role') {
        return children;
    }

    // 3) If user must still pick a role, send them to the picker
    if (shouldPromptRole()) {
        return <Navigate to="/select-role" replace />;
    }

    // 4) If this route restricts roles, ensure activeRole matches
    if (Array.isArray(allowedRoles) && allowedRoles.length > 0) {
        const allowed = allowedRoles.map(r => String(r).toUpperCase());
        if (!activeRole || !allowed.includes(activeRole)) {
            const roles = getRoles();
            if (roles.length > 1) {
                return <Navigate to="/select-role" replace />;
            }
            const role = activeRole || roles[0] || '';
            return <Navigate to={targetPathForRole(role)} replace />;
        }
    }

    // 5) For ADMIN: wait for setup-status, then redirect to wizard if incomplete.
    if (isAdmin && !onSetupPage) {
        if (!setupChecked) {
            return (
                <div className="min-h-[40vh] flex items-center justify-center text-gray-500 text-sm">
                    {t('auth.checkingSetup', { defaultValue: 'Checking academy setup…' })}
                </div>
            );
        }
        if (setupComplete === false && !recentlyFinishedWizard()) {
            return <Navigate to="/setup" replace />;
        }
    }

    // 6) All good
    return children;
};

export default RequireAuth;

